import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormFields } from "../../../../hooks/useFormFields";
import { useSubmitForm } from "../../../../hooks/useSubmitForm";
import { Input } from "../../../../components/common/input";
import { PhoneInput } from "../../../../components/forms/phone-input";
import { Select } from "../../../../components/forms/select";
import { Button } from "../../../../components/common/button";
import { CheckboxField } from "../../../../components/common/checkbox-field";
import { showErrorToast, showSuccessToast } from "../../../../utils/toasts";
import { adminUsersService } from "../../../api_client";
import type {
  AdminCreateUserDto,
  CreatePartnerDto,
  UserDto,
} from "../../../../api/api";
import {
  CreatePartnerDtoPartnerTypeEnum,
  type CreatePartnerDtoPartnerTypeEnum as CreatePartnerDtoPartnerTypeEnumType,
} from "../../../../api/api";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { useDocumentUploadForUser } from "../../hooks/useDocumentUploadForUser";
import { REQUIRED_DOCUMENTS } from "../../../../utils/onboarding";
import { RequiredDocumentsSection } from "../../../onboarding/presentation/components/required-documents-section";
import { Text } from "../../../../components/common/typography";
import { CountryOfOriginSelect as CountrySelect } from "../../../product-requests/presentation/components/country-of-origin-select";
import { StateSelect } from "../../../product-requests/presentation/components/state-select";

type CreatePartnerFormFields = {
  email: string;
  phoneNumber: string;
  companyName: string;
  crNumber: string;
  taxNumber: string;
  contactName: string;
  city: string;
  zip: string;
  street: string;
  street2: string;
  countryId: string;
  stateId: string;
};

function useDebugPartnerAutofill(
  setFormFields: (fields: CreatePartnerFormFields) => void
) {
  const isDebug = import.meta.env.DEV;

  const autofill = useCallback(() => {
    if (!isDebug) return;

    setFormFields({
      email: "debug-partner@example.com",
      phoneNumber: "+15551234567",
      companyName: "Debug Company LLC",
      crNumber: "CR-123456",
      taxNumber: "TAX-987654",
      contactName: "Debug Contact",
      street: "123 Debug Street",
      street2: "Suite 100",
      city: "Debug City",
      zip: "12345",
      countryId: "1",
      stateId: "",
    });
  }, [isDebug, setFormFields]);

  return { isDebug, autofill };
}

export function CreatePartnerForm() {
  const navigate = useNavigate();
  const passwordRef = useRef<HTMLInputElement>(null);
  const [usePassword, setUsePassword] = useState(false);
  const [passwordValue, setPasswordValue] = useState("");
  const [partnerType, setPartnerType] = useState<
    CreatePartnerDtoPartnerTypeEnumType | ""
  >("");

  const { formFields, createChangeHandler, resetFormFields, setFormFields } =
    useFormFields<CreatePartnerFormFields>({
      email: "",
      phoneNumber: "",
      companyName: "",
      crNumber: "",
      taxNumber: "",
      contactName: "",
      city: "",
      zip: "",
      street: "",
      street2: "",
      countryId: "",
      stateId: "",
    });

  const { isDebug, autofill } = useDebugPartnerAutofill(setFormFields);

  const {
    uploading,
    uploadProgress,
    selectedFiles,
    uploadedDocuments,
    handleFileSelect,
    handleFileRemove,
    prepareDocumentsForCreation,
  } = useDocumentUploadForUser();

  const { isLoading, errors, submitHandler } = useSubmitForm<
    AdminCreateUserDto,
    UserDto
  >({
    submitMethod: async () => {
      // Check if there are files to upload before preparing documents
      const hasSelectedFiles = Object.values(selectedFiles).some(
        (file) => file !== null
      );

      // Prepare documents (upload files and get CreateDocumentDto array)
      const documents = hasSelectedFiles
        ? await prepareDocumentsForCreation()
        : [];

      const partner: CreatePartnerDto = {
        name: formFields.companyName || formFields.contactName || "",
        email: formFields.email || undefined,
        phone: formFields.phoneNumber || undefined,
        companyName: formFields.companyName || undefined,
        crNumber: formFields.crNumber || undefined,
        taxNumber: formFields.taxNumber || undefined,
        contactName: formFields.contactName || undefined,
        countryId: formFields.countryId
          ? Number.parseInt(formFields.countryId, 10)
          : undefined,
        stateId: formFields.stateId
          ? Number.parseInt(formFields.stateId, 10)
          : undefined,
        zip: formFields.zip || undefined,
        city: formFields.city || undefined,
        street: formFields.street || undefined,
        street2: formFields.street2 || undefined,
        partnerType: partnerType || undefined,
      };

      const adminCreateUserDto: AdminCreateUserDto = {
        name: formFields.companyName || formFields.contactName || "",
        email: formFields.email || undefined,
        phoneNumber: formFields.phoneNumber || undefined,
        password: usePassword && passwordValue ? passwordValue : undefined,
        documents: documents.length > 0 ? documents : undefined,
        partner,
      };

      return await adminUsersService.createUser(adminCreateUserDto);
    },
    runAfterError: (error) => {
      const errorMessage =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ||
        (error as { message?: string })?.message ||
        "Failed to create partner";
      showErrorToast(errorMessage);
    },
    runAfterSuccess: async (createdUser) => {
      // Check if user has documents (they were auto-approved)
      const hasDocuments =
        createdUser?.documents && createdUser.documents.length > 0;

      if (hasDocuments) {
        showSuccessToast(
          "Partner created and documents uploaded successfully!"
        );
      } else {
        showSuccessToast("Partner created successfully!");
      }

      resetFormFields();
      setPasswordValue("");
      setUsePassword(false);
      setPartnerType("");
      navigate(AppRoutes.PARTNERS);
    },
  });

  const handleCancel = () => {
    navigate(AppRoutes.PARTNERS);
  };

  const generateRandomSuffix = () =>
    Math.floor(100000 + Math.random() * 900000).toString();

  // Button should be enabled if: phone number is entered OR (email AND password are entered)
  const hasPhoneNumber = formFields.phoneNumber.trim().length > 0;
  const hasEmail = formFields.email.trim().length > 0;
  const hasPassword = usePassword && passwordValue.trim().length > 0;
  const hasEmailAndPassword = hasEmail && hasPassword;
  const isFormValid = hasPhoneNumber || hasEmailAndPassword;

  return (
    <form onSubmit={submitHandler} className="w-full">
      <div className="space-y-4">
        <Input
          label="Company Name"
          type="text"
          name="companyName"
          value={formFields.companyName}
          onChange={createChangeHandler("companyName")}
          placeholder="Enter company name"
          required
          error={errors?.partner?.[0]}
         
        />

        <Input
          label="Commercial Registration (CR) Number"
          type="text"
          name="crNumber"
          value={formFields.crNumber}
          onChange={createChangeHandler("crNumber")}
          placeholder="Enter CR number"
          required
          error={errors?.partner?.[0]}
         
        />

        <Input
          label="Tax Number"
          type="text"
          name="taxNumber"
          value={formFields.taxNumber}
          onChange={createChangeHandler("taxNumber")}
          placeholder="Enter tax number"
          required
          error={errors?.partner?.[0]}
         
        />

        <Input
          label="Contact Name"
          type="text"
          name="contactName"
          value={formFields.contactName}
          onChange={createChangeHandler("contactName")}
          placeholder="Enter contact name"
          required
          error={errors?.partner?.[0]}
         
        />

        <Input
          label="Email"
          type="email"
          name="email"
          value={formFields.email}
          onChange={createChangeHandler("email")}
          placeholder="Enter email address"
          autoComplete="email"
          error={errors?.email?.[0]}
          helperText="Optional - user can use OTP login if not provided"
        />

        <PhoneInput
          label="Phone Number"
          value={formFields.phoneNumber || undefined}
          onChange={(value) => {
            setFormFields({
              ...formFields,
              phoneNumber: value || "",
            });
          }}
          error={errors?.phoneNumber?.[0]}
          labelInfo="Optional"
        />

        <Input
          label="Street"
          type="text"
          name="street"
          value={formFields.street}
          onChange={createChangeHandler("street")}
          placeholder="Enter street address"
          error={errors?.partner?.[0]}
        />

        <Input
          label="Street line 2"
          type="text"
          name="street2"
          value={formFields.street2}
          onChange={createChangeHandler("street2")}
          placeholder="Enter suite, unit, building, floor"
          error={errors?.partner?.[0]}
        />

        <Input
          label="City"
          type="text"
          name="city"
          value={formFields.city}
          onChange={createChangeHandler("city")}
          placeholder="Enter city"
          error={errors?.partner?.[0]}
        />

        <Input
          label="ZIP / Postal Code"
          type="text"
          name="zip"
          value={formFields.zip}
          onChange={createChangeHandler("zip")}
          placeholder="Enter ZIP / postal code"
          error={errors?.partner?.[0]}
        />

        <CountrySelect
          value={formFields.countryId}
          onChange={(value) =>
            setFormFields({
              ...formFields,
              countryId: value,
              stateId: "",
            })
          }
          error={errors?.partner?.[0]}
        />

        <StateSelect
          value={formFields.stateId}
          countryId={formFields.countryId}
          onChange={(value) =>
            setFormFields({
              ...formFields,
              stateId: value,
            })
          }
          error={errors?.partner?.[0]}
        />

        <Select
          label="Partner Type"
          name="partnerType"
          value={partnerType}
          onChange={(e) =>
            setPartnerType(
              e.target.value as CreatePartnerDtoPartnerTypeEnumType
            )
          }
          options={[
            { value: CreatePartnerDtoPartnerTypeEnum.Buyer, label: "Buyer" },
            {
              value: CreatePartnerDtoPartnerTypeEnum.Seller,
              label: "Seller",
            },
          ]}
          placeholder="Select partner type"
          helperText="Determines how the partner is created in Odoo"
          error={errors?.partner?.[0]}
        />

        <CheckboxField
          name="usePassword"
          checked={usePassword}
          onChange={(checked) => {
            setUsePassword(checked);
            if (!checked) {
              setPasswordValue("");
            }
          }}
          label="Set password (if unchecked, user will use OTP login)"
          variant="inline"
          className="mb-4"
        />

        {usePassword && (
          <Input
            label="Password"
            type="password"
            name="password"
            value={passwordValue}
            onChange={(e) => setPasswordValue(e.target.value)}
            placeholder="Enter password"
            autoComplete="new-password"
            required={usePassword}
            error={errors?.password?.[0]}
            ref={passwordRef}
            helperText="User will use this password to login first time. They can change it later."
          />
        )}

        {/* Documents Section */}
        <div className="pt-4 border-t border-uiSecondary">
          <Text variant="bodyLg" tone="dark" className="mb-3">
            Documents (Optional)
          </Text>
          <Text variant="body" tone="body" className="mb-4">
            You can upload documents for this partner. Documents will be
            auto-approved and included when the partner is created.
          </Text>
          <RequiredDocumentsSection
            documentTypes={REQUIRED_DOCUMENTS}
            uploading={uploading}
            uploadProgress={uploadProgress}
            selectedFiles={selectedFiles}
            onFileSelect={handleFileSelect}
            onFileRemove={handleFileRemove}
            onConfirmUpload={() => {
              // Documents will be uploaded when partner is created
              // This is just for file selection UI
            }}
            hideUploadButton={true}
            uploadedDocuments={uploadedDocuments}
          />
        </div>

        <div className="flex gap-3 pt-4">
          <Button variant="primary"
            type="submit"
            className="flex-1"
            disabled={isLoading || !isFormValid}
          >
            {isLoading ? "Creating..." : "Create Partner"}
          </Button>
          <Button variant="secondary"
            onClick={handleCancel}
            className="flex-1"
            disabled={isLoading}
          >
            Cancel
          </Button>
        </div>
        {isDebug && (
          <div className="flex gap-3 pt-2">
            <Button variant="secondary"
              onClick={() => {
                autofill();
                setUsePassword(true);
                setPasswordValue("password");
                setFormFields((prev) => ({
                  ...prev,
                  email: `buyer+${generateRandomSuffix()}@example.com`,
                }));
                setPartnerType(CreatePartnerDtoPartnerTypeEnum.Buyer);
              }}
              className="flex-1"
              disabled={isLoading}
            >
              DEV - (Buyer)
            </Button>
            <Button variant="secondary"
              onClick={() => {
                autofill();
                setUsePassword(true);
                setPasswordValue("password");

                setFormFields((prev) => ({
                  ...prev,
                  email: `seller+${generateRandomSuffix()}@example.com`,
                  phoneNumber: `+1555${generateRandomSuffix()}`,
                }));
                setPartnerType(CreatePartnerDtoPartnerTypeEnum.Seller);
              }}
              className="flex-1"
              disabled={isLoading}
            >
              DEV - (Seller)
            </Button>
          </div>
        )}
      </div>
    </form>
  );
}
